import { useEffect, useState } from 'react';
import { useVoiceStore } from '../../stores/voiceStore.js';

const RING_DURATION = 30; // seconds before auto-dismiss

export default function IncomingCallToast() {
  const incomingCall = useVoiceStore(s => s.incomingCall);
  const acceptCall   = useVoiceStore(s => s.acceptCall);
  const declineCall  = useVoiceStore(s => s.declineCall);
  const [remaining, setRemaining] = useState(RING_DURATION);

  useEffect(() => {
    if (!incomingCall) { setRemaining(RING_DURATION); return; }
    setRemaining(RING_DURATION);
    const interval = setInterval(() => setRemaining(r => {
      if (r <= 1) { declineCall(); return RING_DURATION; }
      return r - 1;
    }), 1000);
    return () => clearInterval(interval);
  }, [incomingCall, declineCall]);

  if (!incomingCall) return null;

  const { callerName, callerAvatar, channelName } = incomingCall;
  const initial = (callerName || '?')[0].toUpperCase();
  const pct = ((RING_DURATION - remaining) / RING_DURATION) * 100;

  return (
    <>
      <style>{`
        @keyframes callRing {
          0%, 100% { transform: scale(1); }
          25% { transform: scale(1.08) rotate(-3deg); }
          75% { transform: scale(1.08) rotate(3deg); }
        }
        @keyframes callSlideIn {
          from { opacity: 0; transform: translateY(-20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
      <div style={{
        position: 'fixed', top: 20, right: 20, zIndex: 9000,
        width: 300, borderRadius: 14, overflow: 'hidden',
        background: 'rgba(10,10,14,0.97)', backdropFilter: 'blur(20px)',
        border: '1px solid rgba(34,197,94,0.3)',
        boxShadow: '0 12px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(34,197,94,0.15)',
        animation: 'callSlideIn 0.3s ease-out forwards',
      }}>
        {/* Countdown bar */}
        <div style={{ height: 3, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{ height: '100%', width: `${100 - pct}%`, background: 'linear-gradient(90deg,#22c55e,#16a34a)', transition: 'width 1s linear' }} />
        </div>

        <div style={{ padding: '14px 16px' }}>
          {/* Header */}
          <div style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12 }}>
            📞 Incoming Voice Call
          </div>

          {/* Caller info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700, color: '#fff',
                animation: 'callRing 1.2s ease-in-out infinite',
              }}>
                {callerAvatar || initial}
              </div>
              {/* Ring animation */}
              <div style={{ position: 'absolute', inset: -4, borderRadius: '50%', border: '2px solid rgba(34,197,94,0.4)', animation: 'callRing 1.2s ease-in-out infinite 0.2s' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{callerName || 'Unknown'}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                {channelName ? `🔊 ${channelName}` : 'Voice Channel'} · {remaining}s
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={declineCall}
              style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.25)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
            >
              📵 Decline
            </button>
            <button
              onClick={acceptCall}
              style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.32)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(34,197,94,0.2)'}
            >
              📞 Accept
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
